/**
 * Collection tab (P1-T16).
 *
 * Grid of the user's creations, split into Video / Image tabs. Data comes
 * from GET /ai/jobs (cursor-paginated); each completed job's result upload is
 * resolved for its content type + signed thumbnail URL, then items are
 * filtered client-side by media kind (the API exposes no kind filter).
 *
 * - Tap a card → #/result/:jobId (the result screen owns download/share).
 * - No delete UI: the API has no DELETE /ai/jobs endpoint yet (backend TODO);
 *   deleting the underlying upload would orphan the job row.
 * - Video duration is not exposed by UploadFileMeta, so the ▶ badge shows
 *   without a duration label.
 *
 * Performance (mobile-first): fixed aspect-ratio boxes (no layout shift),
 * lazy images, transform-only shimmer, metadata-only video posters.
 */
import type { AiJob, MediaKind, ToolType, UploadFileMeta } from '@loomia/shared';
import { api } from '../api/client.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';

const PAGE_SIZE = 30;
/** Max extra pages to auto-fetch when the active tab has no items yet. */
const MAX_AUTO_PAGES = 5;

interface ColItem {
  job: AiJob;
  kind: MediaKind;
  /** Signed URL for the thumbnail (image) or the video file itself. */
  src: string;
  /** True → render a <video> poster instead of <img> (no image thumbnail). */
  videoPoster: boolean;
}

type Kind = MediaKind;
type Chip = 'all' | 'image' | 'video' | 'fav' | 'recent';

const TOOL_LABEL: Record<string, string> = {
  image_to_video: 'Image To Video',
  edit_image: 'Edit Image',
  change_outfit: 'Change Outfit',
  face_swap_image: 'Face Swap',
  face_swap_video: 'Face Swap Video',
  hair_salon: 'Hair Salon',
  makeup_styles: 'Makeup Styles',
  face_expression: 'Face Expression',
  photo_enhance: 'Photo Enhance',
  remove_object: 'Remove Object',
  retouch: 'Retouch',
  ingredients: 'Ingredients Video',
  reshape_body: 'Reshape Body',
  combine: 'Combine',
  transition: 'Transition',
};

/** Smart albums: grouped automatically by the tool that made the result. */
const ALBUMS: { id: string; title: string; tools: ToolType[] | null }[] = [
  { id: 'all', title: 'My Creations', tools: null },
  { id: 'video', title: 'Video Clips', tools: ['image_to_video', 'face_swap_video', 'ingredients', 'transition', 'combine'] },
  { id: 'outfits', title: 'Outfits', tools: ['change_outfit'] },
  { id: 'faces', title: 'Face & Hair', tools: ['face_swap_image', 'hair_salon', 'makeup_styles', 'face_expression'] },
  { id: 'edits', title: 'Photo Edits', tools: ['edit_image', 'photo_enhance', 'remove_object', 'retouch', 'reshape_body'] },
];

const RECENT_MS = 7 * 24 * 3_600_000;
const FAV_KEY = 'loomia.favCreations';

function loadFavs(): Set<string> {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveFavs(f: Set<string>): void {
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify([...f]));
  } catch {
    /* private mode: favourites just won't persist */
  }
}

export function renderCollection(root: HTMLElement): void {
  let generation = 0;
  let chip: Chip = 'all';
  let album: string | null = null;
  let query = '';
  const favs = loadFavs();
  let items: ColItem[] = [];
  let cursor: string | null | undefined = undefined; // undefined = not loaded yet
  let loading = false;
  let loadingMore = false;
  let error: string | null = null;

  root.innerHTML = `
    <div class="stack collection">
      <div class="col-head">
        <div>
          <h1 class="cl-title">Collection</h1>
          <p class="cl-sub">Save your creations and favorite templates ✨</p>
        </div>
        <button class="icon-btn" id="col-refresh" aria-label="Refresh collection" title="Refresh">
          <span aria-hidden="true">↻</span>
        </button>
      </div>
      <label class="cl-search">
        ${icon('search')}
        <input id="col-search" type="search" placeholder="Search in your collection..." autocomplete="off" />
      </label>
      <div class="cl-chips" role="tablist" aria-label="Filter">
        <button class="cl-chip active" role="tab" aria-selected="true" data-chip="all">${icon('grid_view')}All</button>
        <button class="cl-chip" role="tab" aria-selected="false" data-chip="image">${icon('imagesmode')}Images</button>
        <button class="cl-chip" role="tab" aria-selected="false" data-chip="video">${icon('play_circle')}Videos</button>
        <button class="cl-chip" role="tab" aria-selected="false" data-chip="fav">${icon('favorite')}Favorites</button>
        <button class="cl-chip" role="tab" aria-selected="false" data-chip="recent">${icon('schedule')}Recently</button>
      </div>
      <div id="col-albums"></div>
      <div class="col-grid" id="col-grid" aria-live="polite"></div>
      <div class="col-foot" id="col-foot"></div>
    </div>
  `;

  const grid = root.querySelector<HTMLElement>('#col-grid')!;
  const foot = root.querySelector<HTMLElement>('#col-foot')!;
  const refreshBtn = root.querySelector<HTMLButtonElement>('#col-refresh')!;
  const albumsEl = root.querySelector<HTMLElement>('#col-albums')!;
  const searchEl = root.querySelector<HTMLInputElement>('#col-search')!;
  const chips = [...root.querySelectorAll<HTMLButtonElement>('.cl-chip')];

  const alive = (gen: number): boolean => gen === generation;

  /** Album/search/chip are all client-side: the API has no filters. */
  function visible(): ColItem[] {
    const tools = album ? (ALBUMS.find((a) => a.id === album)?.tools ?? null) : null;
    const q = query.trim().toLowerCase();
    const cutoff = Date.now() - RECENT_MS;
    return items.filter((i) => {
      if (chip === 'image' && i.kind !== 'image') return false;
      if (chip === 'video' && i.kind !== 'video') return false;
      if (chip === 'fav' && !favs.has(i.job.id)) return false;
      if (chip === 'recent' && new Date(i.job.createdAt).getTime() < cutoff) return false;
      if (tools && !tools.includes(i.job.tool)) return false;
      if (q && !`${TOOL_LABEL[i.job.tool] ?? i.job.tool} ${i.kind}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  function albumsHtml(): string {
    return `
      <div class="cl-albums">
        ${ALBUMS.map((a) => {
          const list = items.filter((i) => !a.tools || a.tools.includes(i.job.tool));
          if (a.tools && list.length === 0) return '';
          const cover = list[0];
          const media = cover
            ? cover.videoPoster
              ? `<video src="${escapeHtml(cover.src)}" muted playsinline preload="metadata"></video>`
              : `<img src="${escapeHtml(cover.src)}" alt="" loading="lazy" decoding="async" />`
            : '';
          return `
            <button class="cl-album" type="button" data-album="${a.id}">
              ${media}
              <span class="cl-album-tx"><strong>${a.title}</strong><small>${icon('imagesmode')}${list.length} item${list.length === 1 ? '' : 's'}</small></span>
              ${icon('chevron_right', { cls: 'cl-album-go' })}
            </button>`;
        }).join('')}
      </div>`;
  }

  function fmtDate(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  }

  function cardHtml(item: ColItem): string {
    const label = `Generated ${item.kind}${fmtDate(item.job.createdAt) ? `, ${escapeHtml(fmtDate(item.job.createdAt))}` : ''}`;
    const media = item.videoPoster
      ? `<video src="${escapeHtml(item.src)}" muted playsinline preload="metadata" data-col-media aria-hidden="true"></video>`
      : `<img src="${escapeHtml(item.src)}" alt="" loading="lazy" decoding="async" data-col-media />`;
    return `
      <a class="col-card" href="#/result/${encodeURIComponent(item.job.id)}" aria-label="${escapeHtml(label)}">
        <span class="col-media">
          <span class="shimmer" aria-hidden="true"></span>
          ${media}
          ${item.kind === 'video' ? '<span class="tpl-media" aria-label="Video">▶</span>' : ''}
        </span>
        <button class="cl-heart${favs.has(item.job.id) ? ' on' : ''}" type="button" data-fav="${escapeHtml(item.job.id)}" aria-label="Favorite" aria-pressed="${favs.has(item.job.id)}">${icon('favorite', { fill: favs.has(item.job.id) })}</button>
      </a>`;
  }

  /** Fade the shimmer out once the thumbnail (img or video poster) is ready. */
  function bindMedia(container: HTMLElement): void {
    container.querySelectorAll('[data-col-media]').forEach((node) => {
      const card = node.closest('.col-card');
      const done = (): void => card?.classList.add('is-loaded');
      const broken = (): void => card?.classList.add('is-broken');
      if (node instanceof HTMLImageElement) {
        if (node.complete && node.naturalWidth > 0) done();
        else {
          node.addEventListener('load', done, { once: true });
          node.addEventListener('error', broken, { once: true });
        }
      } else if (node instanceof HTMLVideoElement) {
        if (node.readyState >= 2) done();
        else {
          node.addEventListener('loadeddata', done, { once: true });
          node.addEventListener('error', broken, { once: true });
        }
      }
    });
  }

  function skeletonHtml(count: number): string {
    return Array.from(
      { length: count },
      () =>
        `<div class="col-card is-skeleton" aria-hidden="true"><span class="col-media"><span class="shimmer"></span></span></div>`,
    ).join('');
  }

  function render(): void {
    const list = visible();
    refreshBtn.disabled = loading;

    if (loading && items.length === 0) {
      grid.innerHTML = skeletonHtml(9);
      foot.innerHTML = '';
      return;
    }

    if (error && items.length === 0) {
      grid.innerHTML = '';
      foot.innerHTML = `
        <div class="empty">
          <div class="empty-icon">⚠️</div>
          <p class="muted" style="max-width: 280px;">${escapeHtml(error)}</p>
          <button class="btn btn-secondary" id="col-retry">Try again</button>
        </div>`;
      foot.querySelector('#col-retry')?.addEventListener('click', () => void load(true));
      return;
    }

    const showAlbums = chip === 'all' && !album && !query.trim() && items.length > 0;
    albumsEl.innerHTML = showAlbums
      ? albumsHtml()
      : album
        ? `<button class="cl-back" type="button" data-album="">${icon('arrow_back')}${escapeHtml(ALBUMS.find((a) => a.id === album)?.title ?? 'Back')}</button>`
        : '';
    grid.innerHTML = (showAlbums ? '<h2 class="section-title cl-recent">Recent</h2>' : '') + list.map(cardHtml).join('');
    grid.classList.toggle('with-title', showAlbums);
    bindMedia(grid);

    if (list.length === 0 && !loadingMore) {
      const isVideo = chip === 'video';
      foot.innerHTML = `
        <div class="empty">
          <div class="empty-icon">${isVideo ? '🎬' : '🖼️'}</div>
          <p class="muted" style="max-width: 280px;">
            ${chip === 'fav' ? 'Tap the heart on a creation to save it here.' : query.trim() ? 'No matches. Try another word.' : isVideo ? 'No videos yet — create your first.' : 'Nothing here yet — create your first.'}
          </p>
          <a class="btn btn-primary" href="#/create">Create now</a>
        </div>`;
    } else if (cursor) {
      foot.innerHTML = `
        <button class="btn btn-secondary btn-block" id="col-more" ${loadingMore ? 'disabled' : ''}>
          ${loadingMore ? 'Loading…' : 'Load more'}
        </button>`;
      foot.querySelector('#col-more')?.addEventListener('click', () => void load(false));
    } else {
      foot.innerHTML = '';
    }
  }

  async function resolveItems(jobs: AiJob[]): Promise<ColItem[]> {
    const done = jobs.filter((j) => j.state === 'done' && j.resultFileId);
    const metas = await Promise.allSettled(
      done.map((j) => api.getUpload(j.resultFileId as string)),
    );
    const out: ColItem[] = [];
    done.forEach((job, idx) => {
      const r = metas[idx];
      if (r.status !== 'fulfilled') return;
      const meta: UploadFileMeta = r.value;
      const ct = meta.contentType || '';
      const itemKind: Kind | null = ct.startsWith('video/')
        ? 'video'
        : ct.startsWith('image/')
          ? 'image'
          : null;
      if (!itemKind) return;
      const thumb = meta.thumbnails?.thumb256 ?? meta.url ?? '';
      if (!thumb) return;
      out.push({
        job,
        kind: itemKind,
        src: thumb,
        // Videos rarely have image thumbnails; use the file itself as a poster.
        videoPoster: itemKind === 'video' && !meta.thumbnails?.thumb256,
      });
    });
    return out;
  }

  /** Fetch one page (reset=true starts over). Auto-advances while the active
   *  tab is empty and more pages exist (bounded). */
  async function load(reset: boolean, autoPages = 0): Promise<void> {
    const gen = generation;
    if (reset) {
      items = [];
      cursor = undefined;
      error = null;
    }
    if (reset) loading = true;
    else loadingMore = true;
    render();

    try {
      const page = await api.listJobs({
        limit: PAGE_SIZE,
        cursor: reset ? undefined : (cursor ?? undefined),
      });
      if (!alive(gen)) return;
      const fresh = await resolveItems(page.jobs);
      if (!alive(gen)) return;
      const seen = new Set(items.map((i) => i.job.id));
      for (const it of fresh) {
        if (!seen.has(it.job.id)) {
          seen.add(it.job.id);
          items.push(it);
        }
      }
      cursor = page.nextCursor ?? null;
      error = null;
    } catch (e) {
      if (!alive(gen)) return;
      error =
        e instanceof Error && e.message
          ? e.message
          : 'Could not load your collection. Check your connection.';
    } finally {
      // no-unsafe-finally: never `return` from finally (it would swallow an
      // in-flight throw). Gate the block instead — identical behavior.
      if (alive(gen)) {
        loading = false;
        loadingMore = false;
        render();
        // If this tab shows nothing but more pages exist, keep fetching.
        if (!error && visible().length === 0 && cursor && autoPages < MAX_AUTO_PAGES) {
          void load(false, autoPages + 1);
        }
      }
    }
  }

  for (const c of chips) {
    c.addEventListener('click', () => {
      chip = (c.dataset['chip'] as Chip) ?? 'all';
      if (chip !== 'all') album = null;
      for (const t of chips) {
        const active = t === c;
        t.classList.toggle('active', active);
        t.setAttribute('aria-selected', String(active));
      }
      render();
      if (!error && visible().length === 0 && cursor && !loading && !loadingMore) void load(false);
    });
  }

  searchEl.addEventListener('input', () => {
    query = searchEl.value;
    render();
  });

  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const heart = t.closest<HTMLElement>('[data-fav]');
    if (heart) {
      e.preventDefault();
      e.stopPropagation();
      const id = heart.dataset['fav']!;
      if (favs.has(id)) favs.delete(id);
      else favs.add(id);
      saveFavs(favs);
      render();
      return;
    }
    const al = t.closest<HTMLElement>('[data-album]');
    if (al) {
      const id = al.dataset['album'] ?? '';
      album = id && id !== 'all' ? id : null;
      if (id === 'all') {
        // "My Creations" = everything, as a flat grid.
        album = 'all';
      }
      render();
      window.scrollTo({ top: 0 });
    }
  });

  refreshBtn.addEventListener('click', () => {
    generation += 1;
    void load(true);
  });

  void load(true);
}
